import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Grid, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { AreaChart, BarChart, CompositeChart, DonutChart, LineChart } from '@mantine/charts';
import {
  IconEye, IconHeart, IconInfoCircle, IconPercentage, IconUserPlus, IconUsers,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { FadeIn } from '@/components/animations/transitions';
import {
  AnalyticsChartCard, AnalyticsToolbar, MetricDeltaCard, SocialEmptyState,
} from '../components';
import { useSocialMediaFilters } from '@/hooks/socialmedia/useSocialMediaFilters';
import { useSocialMediaAccounts } from '@/hooks/socialmedia/useSocialMediaAccounts';
import { useSocialAnalytics } from '@/hooks/socialmedia/useSocialAnalytics';
import { socialKeys } from '../utils/queryKeys';
import {
  CHART_HEIGHT, FILTER_PAGE_KEYS, PLATFORM_COLOR, type Platform,
} from '../utils/socialMediaConstants';
import { formatCompact, formatPercent, periodLabel } from '../utils/analyticsHelpers';

/**
 * Cross-platform overview.
 *
 * Every chart reads from one daily-metrics series, so a single date range drives the
 * whole page and there is no per-chart fetching to keep in sync.
 */
export default function SocialMediaDashboard() {
  const queryClient = useQueryClient();

  const { filter, comparisonFilter, setFilter } = useSocialMediaFilters(FILTER_PAGE_KEYS.DASHBOARD);
  const { accounts, accountOptions, noAccounts, isLoading: accountsLoading } = useSocialMediaAccounts();

  const accountIds = useMemo(() => accounts.map((a) => String(a.id)), [accounts]);
  const { rows, kpis, isLoading, isFetching, isEmpty } =
    useSocialAnalytics(filter, comparisonFilter, accountIds);

  /** Buckets the daily rows by the selected granularity. */
  const series = useMemo(() => {
    const bucketOf = (date: string) => {
      const d = dayjs(date);
      if (filter.granularity === 'month') return d.format('MMM YYYY');
      // Labelled by the week's start date rather than an ISO week number: dayjs's
      // isoWeek plugin is not registered app-wide, and "6 Sep" reads better on an axis
      // than "W36" anyway.
      if (filter.granularity === 'week') return d.startOf('week').format('D MMM');
      return d.format('D MMM');
    };

    const buckets = new Map<string, {
      period: string; views: number; viewers: number; organic: number; paid: number;
      likes: number; comments: number; shares: number; engagement: number;
      followers: number; netChange: number; posts: number;
    }>();

    for (const r of [...rows].sort((a, b) => a.metricDate.localeCompare(b.metricDate))) {
      const key = bucketOf(r.metricDate);
      const b = buckets.get(key) ?? {
        period: key, views: 0, viewers: 0, organic: 0, paid: 0,
        likes: 0, comments: 0, shares: 0, engagement: 0, followers: 0, netChange: 0, posts: 0,
      };

      b.views += (r.mediaViews ?? 0) + (r.totalViews ?? 0);
      b.viewers += (r.mediaViewers ?? 0) + (r.totalReach ?? 0);
      b.organic += (r.mediaViewsOrganic ?? r.organicReach ?? 0);
      b.paid += (r.mediaViewsFromAds ?? r.paidReach ?? 0);
      b.likes += r.totalLikes ?? 0;
      b.comments += r.totalComments ?? 0;
      b.shares += r.totalShares ?? 0;
      b.engagement += r.totalEngagement ?? 0;
      b.netChange += r.netFollowerChange ?? 0;
      b.posts += r.postsPublished ?? 0;
      // Follower count is a level: the latest value in the bucket wins, never a sum.
      b.followers = Math.max(b.followers, r.followerCount ?? 0);

      buckets.set(key, b);
    }

    return Array.from(buckets.values());
  }, [rows, filter.granularity]);

  /** Totals per platform, for the comparison bar and the audience split. */
  const byPlatform = useMemo(() => {
    const map = new Map<string, { platform: string; views: number; engagement: number; followers: number }>();
    for (const r of rows) {
      const key = r.platform ?? 'Unknown';
      const p = map.get(key) ?? { platform: key, views: 0, engagement: 0, followers: 0 };
      p.views += (r.mediaViews ?? 0) + (r.totalViews ?? 0);
      p.engagement += r.totalEngagement ?? 0;
      p.followers = Math.max(p.followers, r.followerCount ?? 0);
      map.set(key, p);
    }
    return Array.from(map.values());
  }, [rows]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: socialKeys.analytics() });

  if (!accountsLoading && noAccounts) {
    return (
      <Stack gap="md">
        <Header />
        <SocialEmptyState noAccounts />
      </Stack>
    );
  }

  const chartsEmpty = isEmpty || series.length === 0;

  return (
    <FadeIn>
      <Stack gap="md">
        <Header subtitle={periodLabel(filter)} />

        <AnalyticsToolbar
          filter={filter}
          onChange={setFilter}
          onRefresh={refresh}
          accountOptions={accountOptions}
          loading={isFetching}
        />

        {/* Meta's 2026 metric change is not a dashboard bug, so say so once, here. */}
        <Alert
          type="info"
          icon={<IconInfoCircle size={16} />}
          title="About Views and Viewers"
        >
          <Text size="sm">
            Meta replaced organic impressions and reach with <strong>Views</strong> and{' '}
            <strong>Viewers</strong> during 2026. Viewers is the closest equivalent to the old
            Reach, but Meta states it is not a like-for-like substitute — figures spanning that
            change are not directly comparable.
          </Text>
        </Alert>

        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
          <MetricDeltaCard
            label="Followers" value={formatCompact(kpis.followers.current)}
            delta={kpis.followers} icon={<IconUsers size={14} />}
            hint="Audience size at the end of the period. A level, not a total — never summed across days."
          />
          <MetricDeltaCard
            label="Net growth" value={formatCompact(kpis.netFollowerChange.current)}
            delta={kpis.netFollowerChange} icon={<IconUserPlus size={14} />} color="teal"
            hint="New followers minus unfollows over the period."
          />
          <MetricDeltaCard
            label="Views" value={formatCompact(kpis.views.current)}
            delta={kpis.views} icon={<IconEye size={14} />} color="grape"
            hint="Times your content was seen. Replaces the retired impressions metric."
          />
          <MetricDeltaCard
            label="Viewers" value={formatCompact(kpis.viewers.current)}
            delta={kpis.viewers} icon={<IconUsers size={14} />} color="indigo"
            hint="Unique accounts that saw your content. Closest equivalent to the old Reach."
          />
          <MetricDeltaCard
            label="Engagements" value={formatCompact(kpis.engagement.current)}
            delta={kpis.engagement} icon={<IconHeart size={14} />} color="pink"
            hint="Reactions, comments, shares and saves combined."
          />
          <MetricDeltaCard
            label="Engagement rate" value={formatPercent(kpis.engagementRate.current, 2)}
            delta={kpis.engagementRate} icon={<IconPercentage size={14} />} color="orange"
            hint="Engagements ÷ viewers for the whole period — not an average of daily rates."
          />
        </SimpleGrid>

        <Grid>
          <Grid.Col span={12}>
            <AnalyticsChartCard
              title="Audience growth"
              subtitle="Follower count with daily net change"
              loading={isLoading} empty={chartsEmpty}
            >
              <CompositeChart
                h={CHART_HEIGHT}
                data={series}
                dataKey="period"
                maxBarWidth={24}
                series={[
                  { name: 'netChange', label: 'Net change', color: 'teal.6', type: 'bar' },
                  { name: 'followers', label: 'Followers', color: 'blue.6', type: 'line', yAxisId: 'right' },
                ]}
                withRightYAxis
                withLegend
                curveType="monotone"
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Views: organic vs paid"
              subtitle="Where your reach came from"
              loading={isLoading} empty={chartsEmpty}
            >
              <AreaChart
                h={CHART_HEIGHT}
                data={series}
                dataKey="period"
                type="stacked"
                withLegend
                curveType="monotone"
                series={[
                  { name: 'organic', label: 'Organic', color: 'blue.5' },
                  { name: 'paid', label: 'Paid', color: 'orange.5' },
                ]}
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Views and viewers"
              subtitle="Total views against unique viewers"
              loading={isLoading} empty={chartsEmpty}
            >
              <LineChart
                h={CHART_HEIGHT}
                data={series}
                dataKey="period"
                withLegend
                curveType="monotone"
                series={[
                  { name: 'views', label: 'Views', color: 'grape.6' },
                  { name: 'viewers', label: 'Viewers', color: 'indigo.6' },
                ]}
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Engagement mix"
              subtitle="What people actually did"
              loading={isLoading} empty={chartsEmpty}
            >
              <BarChart
                h={CHART_HEIGHT}
                data={series}
                dataKey="period"
                type="stacked"
                withLegend
                series={[
                  { name: 'likes', label: 'Reactions', color: 'pink.5' },
                  { name: 'comments', label: 'Comments', color: 'violet.5' },
                  { name: 'shares', label: 'Shares', color: 'cyan.5' },
                ]}
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Posting cadence vs engagement"
              subtitle="Does posting more actually help?"
              loading={isLoading} empty={chartsEmpty}
            >
              <CompositeChart
                h={CHART_HEIGHT}
                data={series}
                dataKey="period"
                maxBarWidth={24}
                withLegend
                curveType="monotone"
                series={[
                  { name: 'posts', label: 'Posts published', color: 'gray.5', type: 'bar' },
                  { name: 'engagement', label: 'Engagements', color: 'pink.6', type: 'line', yAxisId: 'right' },
                ]}
                withRightYAxis
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Platform comparison"
              subtitle="Views and engagement by platform"
              loading={isLoading} empty={byPlatform.length === 0}
            >
              <BarChart
                h={CHART_HEIGHT}
                data={byPlatform}
                dataKey="platform"
                withLegend
                series={[
                  { name: 'views', label: 'Views', color: 'grape.6' },
                  { name: 'engagement', label: 'Engagements', color: 'pink.6' },
                ]}
              />
            </AnalyticsChartCard>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <AnalyticsChartCard
              title="Audience split"
              subtitle="Followers by platform"
              loading={isLoading} empty={byPlatform.length === 0}
            >
              <Group justify="center" h={CHART_HEIGHT} align="center">
                <DonutChart
                  size={200}
                  thickness={30}
                  withLabelsLine
                  withTooltip
                  data={byPlatform.map((p) => ({
                    name: p.platform,
                    value: p.followers,
                    color: `${PLATFORM_COLOR[p.platform as Platform] ?? 'gray'}.6`,
                  }))}
                />
              </Group>
            </AnalyticsChartCard>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );

  function Header({ subtitle }: { subtitle?: string }) {
    return (
      <Group justify="space-between" align="center">
        <div>
          <Title order={2}>Social Media Analytics</Title>
          <Text size="sm" c="dimmed">
            {subtitle ?? 'Performance across your connected accounts'}
          </Text>
        </div>
      </Group>
    );
  }
}
